import { useEffect, useState } from 'react';
import { getUnreadCount } from '@/features/notifications/api';

/**
 * Polling hook for the bell-badge unread-notification count.
 *
 * Production-readiness disciplines (Batch 9C Gate-1 polling addition):
 *   1. 30-second polling interval (NOT aggressive 5-10s polls)
 *   2. Pause polling when the tab is hidden (Page Visibility API)
 *   3. Stop polling when offline (navigator.onLine === false)
 *   4. Resume polling when tab becomes visible AND online again
 *
 * Without these guardrails, the bell-badge becomes a backend-load
 * problem at scale.
 *
 * Migration target: tracker entry "Notification unread-count: SSE or
 * WebSocket migration" (PWA/Offen #35) — replace polling with a
 * server-sent stream once user volume warrants real-time semantics.
 *
 * Module-level cache + inflight dedupe pattern mirrors useEntitlement
 * + useSubscriptionState — multiple TopBar mounts share one fetch.
 */

const POLL_INTERVAL_MS = 30_000;

let cachedCount: number | null = null;
let inflight: Promise<number> | null = null;

export function clearUnreadCountCache(): void {
  cachedCount = null;
  inflight = null;
}

async function fetchUnreadCount(): Promise<number> {
  if (inflight) return inflight;
  inflight = getUnreadCount()
    .then((res) => {
      cachedCount = res.count;
      return res.count;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export interface UseUnreadNotificationCountResult {
  count: number;
  isLoading: boolean;
}

export function useUnreadNotificationCount(): UseUnreadNotificationCountResult {
  const [count, setCount] = useState<number>(cachedCount ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(cachedCount === null);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | null = null;

    const isVisible = () =>
      typeof document !== 'undefined' && document.visibilityState !== 'hidden';
    const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

    const tick = async () => {
      if (cancelled) return;
      if (!isVisible() || !isOnline()) return;
      try {
        const next = await fetchUnreadCount();
        if (cancelled) return;
        setCount(next);
        setIsLoading(false);
      } catch {
        // Swallow — bell-badge is best-effort. Last-known value stays
        // visible. Next interval retries.
        if (!cancelled) setIsLoading(false);
      }
    };

    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    };
    const stopInterval = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibility = () => {
      if (isVisible() && isOnline()) {
        // Resume: fetch immediately + restart interval
        void tick();
        startInterval();
      } else {
        stopInterval();
      }
    };
    const handleOnline = () => {
      if (isVisible() && isOnline()) {
        void tick();
        startInterval();
      }
    };
    const handleOffline = () => {
      stopInterval();
    };

    // Initial fetch on mount, regardless of visibility (TopBar bell
    // shows last-known on first paint; if no cache, default-0 paint
    // and the fetch fills it on resolve).
    void tick();
    if (isVisible() && isOnline()) {
      startInterval();
    }

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { count, isLoading };
}
